<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-4 page-header">
      <v-card-title class="text-h5 font-weight-bold pa-0">
        <v-icon left color="#4a3aff">mdi-account-tie</v-icon>
        {{ $t('admin.managers.title') }}
      </v-card-title>
      <v-btn color="primary" :loading="loading" @click="loadAll">
        <v-icon left>mdi-refresh</v-icon>
        {{ $t('common.refresh') }}
      </v-btn>
    </div>

    <v-breadcrumbs :items="breadcrumbs" class="px-0 pt-0 mb-3">
      <template #item="{ item }">
        <v-breadcrumbs-item
          :disabled="item.disabled"
          @click="item.action && item.action()"
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <template v-if="!selectedCompany">
      <v-row>
        <v-col cols="12" lg="4">
          <v-card class="pa-5 rounded-lg">
            <v-card-title class="px-0 pt-0 text-h6">
              <v-icon left color="#4a3aff">mdi-domain-plus</v-icon>
              {{ $t('admin.managers.companyFormTitle') }}
            </v-card-title>

            <v-form ref="companyForm" v-model="companyValid">
              <v-text-field
                v-model="companyForm.name"
                :label="$t('admin.managers.companyName')"
                outlined
                dense
                prepend-inner-icon="mdi-domain"
                :rules="[rules.required]"
              />
              <v-text-field
                v-model="companyForm.email"
                :label="$t('admin.managers.contactEmail')"
                outlined
                dense
                type="email"
                prepend-inner-icon="mdi-email"
                :rules="[rules.emailOptional]"
              />
              <v-text-field
                v-model="companyForm.phone"
                :label="$t('common.phone')"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              />
              <v-text-field
                v-model="companyForm.address"
                :label="$t('common.address')"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />

              <v-btn color="primary" block :loading="savingCompany" @click="createCompany">
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('admin.managers.companyFormTitle') }}
              </v-btn>
            </v-form>
          </v-card>
        </v-col>

        <v-col cols="12" lg="8">
          <v-card class="pa-5 rounded-lg">
            <v-card-title class="px-0 pt-0 text-h6">
              <v-icon left color="#4a3aff">mdi-domain</v-icon>
              {{ $t('common.companies') }}
            </v-card-title>

            <v-data-table
              :headers="companyHeaders"
              :items="companies"
              :loading="loading"
              dense
              class="elevation-0"
              @click:row="openCompany"
            >
              <template #[`item.managersCount`]="{ item }">
                <v-chip small color="deep-purple lighten-5">
                  {{ managersCount(item.id) }}
                </v-chip>
              </template>

              <template #[`item.createdAt`]="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template #[`item.actions`]="{ item }">
                <div class="d-flex justify-end">
                  <v-btn
                    icon
                    small
                    color="red"
                    :loading="deletingCompanyId === item.id"
                    @click.stop="deleteCompany(item)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-btn icon small color="primary" @click.stop="openCompany(item)">
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <div class="d-flex align-center justify-space-between mb-4 company-header">
        <div>
          <h2 class="text-h6 font-weight-bold mb-1">{{ selectedCompany.name }}</h2>
          <div class="text-body-2 grey--text text--darken-1">
            {{ selectedCompany.email || selectedCompany.phone || selectedCompany.address || $t('admin.managers.companyManagers') }}
          </div>
        </div>
        <v-btn text color="primary" @click="backToCompanies">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('admin.managers.backToCompanies') }}
        </v-btn>
      </div>

      <v-row>
        <v-col cols="12" lg="4">
          <v-card class="pa-5 rounded-lg">
            <v-card-title class="px-0 pt-0 text-h6">
              <v-icon left color="#4a3aff">mdi-account-plus</v-icon>
              {{ $t('admin.managers.managerFormTitle') }}
            </v-card-title>

            <v-form ref="managerForm" v-model="managerValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="managerForm.nombre"
                    :label="$t('common.name')"
                    outlined
                    dense
                    prepend-inner-icon="mdi-account"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="managerForm.apellidos"
                    :label="$t('common.lastName')"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>

              <v-text-field
                v-model="managerForm.email"
                :label="$t('common.email')"
                outlined
                dense
                type="email"
                prepend-inner-icon="mdi-email"
                :rules="[rules.required, rules.email]"
              />

              <v-btn-toggle v-model="managerForm.accessMode" mandatory class="mb-4">
                <v-btn value="password">
                  <v-icon left>mdi-lock</v-icon>
                  {{ $t('admin.managers.password') }}
                </v-btn>
                <v-btn value="google">
                  <v-icon left>mdi-google</v-icon>
                  Google
                </v-btn>
              </v-btn-toggle>

              <v-text-field
                v-if="managerForm.accessMode === 'password'"
                v-model="managerForm.password"
                :label="$t('admin.managers.temporaryPassword')"
                outlined
                dense
                :type="showManagerPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock"
                :append-icon="showManagerPassword ? 'mdi-eye-off' : 'mdi-eye'"
                :rules="[rules.password]"
                @click:append="showManagerPassword = !showManagerPassword"
              />

              <v-btn color="primary" block :loading="savingManager" @click="createManager">
                <v-icon left>mdi-account-check</v-icon>
                {{ $t('admin.managers.managerFormTitle') }}
              </v-btn>
            </v-form>
          </v-card>
        </v-col>

        <v-col cols="12" lg="8">
          <v-card class="pa-5 rounded-lg">
            <v-card-title class="px-0 pt-0 text-h6">
              <v-icon left color="#4a3aff">mdi-account-multiple</v-icon>
              {{ $t('admin.managers.companyManagers') }}
            </v-card-title>

            <v-data-table
              :headers="managerHeaders"
              :items="companyManagers"
              :loading="loading"
              dense
              class="elevation-0"
            >
              <template #[`item.access`]="{ item }">
                <v-chip small :color="item.source === 'invite' ? 'blue lighten-4' : 'green lighten-4'">
                  {{ item.source === 'invite' ? $t('admin.managers.googlePending') : item.access }}
                </v-chip>
              </template>

              <template #[`item.createdAt`]="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template #[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  color="red"
                  :loading="deletingManagerId === item.id"
                  @click.stop="deleteManager(item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>

              <template #no-data>
                {{ $t('admin.managers.emptyManagers') }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-dialog v-model="dialogConfirm" max-width="460px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ $t('common.confirmAction') }}
        </v-card-title>

        <v-card-text>
          <v-alert v-if="confirmWarning" type="warning" dense border="left" class="mb-4">
            {{ confirmWarning }}
          </v-alert>
          {{ confirmMessage }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="dialogConfirm = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="red" dark :loading="confirmLoading" @click="runConfirmAction">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { db, managerCreationAuthReady } from "@/firebase";
import { createUserWithEmailAndPassword, sendEmailVerification, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";

const emptyCompanyForm = () => ({
  name: "",
  email: "",
  phone: "",
  address: "",
});

const emptyManagerForm = (companyId = "") => ({
  companyId,
  nombre: "",
  apellidos: "",
  email: "",
  password: "",
  accessMode: "password",
});

const batchLimit = 450;

export default {
  name: "Managers",

  data() {
    return {
      loading: false,
      savingCompany: false,
      savingManager: false,
      companyValid: false,
      managerValid: false,
      message: "",
      messageType: "success",
      selectedCompanyId: "",
      showManagerPassword: false,
      dialogConfirm: false,
      confirmMessage: "",
      confirmWarning: "",
      confirmAction: null,
      deletingCompanyId: "",
      deletingManagerId: "",
      companies: [],
      managers: [],
      companyForm: emptyCompanyForm(),
      managerForm: emptyManagerForm(),
    };
  },

  computed: {
    selectedCompany() {
      return this.companies.find((company) => company.id === this.selectedCompanyId) || null;
    },
    companyManagers() {
      if (!this.selectedCompanyId) return [];
      return this.managers.filter((manager) => manager.companyId === this.selectedCompanyId);
    },
    confirmLoading() {
      return !!this.deletingCompanyId || !!this.deletingManagerId;
    },
    breadcrumbs() {
      const items = [
        {
          text: this.$t("common.companies"),
          disabled: !this.selectedCompany,
          action: this.selectedCompany ? this.backToCompanies : null,
        },
      ];

      if (this.selectedCompany) {
        items.push({
          text: this.selectedCompany.name || this.$t("common.company"),
          disabled: true,
        });
      }

      return items;
    },
    companyHeaders() {
      return [
        { text: this.$t("common.company"), value: "name" },
        { text: this.$t("common.email"), value: "email" },
        { text: this.$t("common.phone"), value: "phone" },
        { text: this.$t("appShell.nav.managers"), value: "managersCount", align: "center" },
        { text: this.$t("common.createdAt"), value: "createdAt" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    managerHeaders() {
      return [
        { text: this.$t("common.name"), value: "fullName" },
        { text: this.$t("common.email"), value: "email" },
        { text: this.$t("common.access"), value: "access" },
        { text: this.$t("common.createdAt"), value: "createdAt" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
    rules() {
      return {
        required: (value) => !!value || this.$t("common.required"),
        email: (value) => /.+@.+\..+/.test(value) || this.$t("common.invalidEmail"),
        emailOptional: (value) => !value || /.+@.+\..+/.test(value) || this.$t("common.invalidEmail"),
        password: (value) => !!value && value.length >= 6 || this.$t("common.minPassword"),
      };
    },
  },

  async created() {
    await this.loadAll();
  },

  methods: {
    async loadAll() {
      this.loading = true;

      try {
        await this.loadCompanies();
        await this.loadManagers();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "admin.managers.loadError", this.$i18n), "error");
      } finally {
        this.loading = false;
      }
    },

    async loadCompanies() {
      const snap = await getDocs(collection(db, "companies"));
      this.companies = snap.docs
        .map((companyDoc) => ({ id: companyDoc.id, ...companyDoc.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      if (this.selectedCompanyId && !this.selectedCompany) {
        this.backToCompanies();
      }
    },

    async loadManagers() {
      const companyById = new Map(this.companies.map((company) => [company.id, company]));
      const usersSnap = await getDocs(query(collection(db, "users"), where("role", "==", "admin")));
      const invitesSnap = await getDocs(collection(db, "adminInvites"));

      const users = usersSnap.docs.map((userDoc) => {
        const data = userDoc.data();
        const company = companyById.get(data.companyId);

        return {
          id: userDoc.id,
          ...data,
          fullName: `${data.nombre || ""} ${data.apellidos || ""}`.trim() || data.email,
          companyName: data.companyName || company?.name || "",
          access: data.access || this.$t("admin.managers.passwordGoogle"),
          source: "user",
        };
      });
      const acceptedManagerKeys = new Set(
        users.map((user) => this.managerIdentityKey(user.email, user.companyId))
      );

      const invites = invitesSnap.docs
        .map((inviteDoc) => {
          const data = inviteDoc.data();
          const company = companyById.get(data.companyId);

          return {
            id: inviteDoc.id,
            ...data,
            fullName: `${data.nombre || ""} ${data.apellidos || ""}`.trim() || data.email,
            companyName: data.companyName || company?.name || "",
            access: "google",
            source: "invite",
          };
        })
        .filter((invite) => !acceptedManagerKeys.has(this.managerIdentityKey(invite.email, invite.companyId)));

      this.managers = [...users, ...invites]
        .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
    },

    openCompany(company) {
      this.selectedCompanyId = company.id;
      this.managerForm = emptyManagerForm(company.id);
      this.message = "";
      this.$nextTick(() => this.$refs.managerForm?.resetValidation());
    },

    backToCompanies() {
      this.selectedCompanyId = "";
      this.managerForm = emptyManagerForm();
      this.message = "";
    },

    managersCount(companyId) {
      return this.managers.filter((manager) => manager.companyId === companyId).length;
    },

    managerIdentityKey(email, companyId) {
      return `${(email || "").trim().toLowerCase()}_${companyId || ""}`;
    },

    async findUserByEmail(email) {
      const normalizedEmail = (email || "").trim();
      const lowerEmail = normalizedEmail.toLowerCase();
      const emailCandidates = normalizedEmail === lowerEmail
        ? [normalizedEmail]
        : [normalizedEmail, lowerEmail];

      for (const candidate of emailCandidates) {
        const snap = await getDocs(query(collection(db, "users"), where("email", "==", candidate)));
        if (!snap.empty) return snap.docs[0];
      }

      return null;
    },

    async createCompany() {
      if (!this.$refs.companyForm.validate()) return;

      this.savingCompany = true;

      try {
        const companyRef = await addDoc(collection(db, "companies"), {
          name: this.companyForm.name.trim(),
          email: this.companyForm.email.trim(),
          phone: this.companyForm.phone.trim(),
          address: this.companyForm.address.trim(),
          createdAt: serverTimestamp(),
          createdByUid: this.$store.getters["auth/user"]?.uid || "",
        });

        this.companyForm = emptyCompanyForm();
        this.$nextTick(() => this.$refs.companyForm.resetValidation());
        await this.loadCompanies();

        const createdCompany = this.companies.find((company) => company.id === companyRef.id);
        if (createdCompany) this.openCompany(createdCompany);

        this.showMessage(this.$t("admin.managers.createdCompany"), "success");
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "admin.managers.companyCreateError", this.$i18n), "error");
      } finally {
        this.savingCompany = false;
      }
    },

    async createManager() {
      if (!this.selectedCompany) {
        this.showMessage(this.$t("admin.managers.selectValidCompany"), "error");
        return;
      }

      this.managerForm.companyId = this.selectedCompany.id;
      if (!this.$refs.managerForm.validate()) return;

      this.savingManager = true;

      try {
        let createdMessageKey = "admin.managers.createdManager";
        const now = new Date().toISOString();
        const profile = {
          nombre: this.managerForm.nombre.trim(),
          apellidos: this.managerForm.apellidos.trim(),
          email: this.managerForm.email.trim(),
          role: "admin",
          companyId: this.selectedCompany.id,
          companyName: this.selectedCompany.name || "",
          access: this.managerForm.accessMode,
          createdByUid: this.$store.getters["auth/user"]?.uid || "",
        };
        const existingUserDoc = await this.findUserByEmail(profile.email);

        if (existingUserDoc) {
          await updateDoc(existingUserDoc.ref, {
            ...profile,
            promotedToAdminAt: now,
            updatedAt: now,
          });
          await deleteDoc(doc(db, "adminInvites", profile.email.toLowerCase()));
          createdMessageKey = "admin.managers.promotedExistingManager";
        } else if (this.managerForm.accessMode === "password") {
          const managerAuth = await managerCreationAuthReady;
          const credential = await createUserWithEmailAndPassword(
            managerAuth,
            profile.email,
            this.managerForm.password
          );

          await setDoc(doc(db, "users", credential.user.uid), {
            ...profile,
            createdAt: now,
          });
          await sendEmailVerification(credential.user);
          await signOut(managerAuth);
          createdMessageKey = "admin.managers.createdManagerWithVerification";
        } else {
          await setDoc(doc(db, "adminInvites", profile.email.toLowerCase()), {
            ...profile,
            createdAt: now,
            status: "pending",
          });
        }

        this.managerForm = emptyManagerForm(this.selectedCompany.id);
        this.$nextTick(() => this.$refs.managerForm.resetValidation());
        await this.loadManagers();
        this.showMessage(this.$t(createdMessageKey), "success");
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "admin.managers.managerCreateError", this.$i18n), "error");
      } finally {
        this.savingManager = false;
      }
    },

    deleteCompany(company) {
      this.openConfirm(
        this.$t("admin.managers.deleteCompanyConfirm", { name: company.name || this.$t("common.company") }),
        async () => {
          this.deletingCompanyId = company.id;

          try {
            await this.deleteCompanyCascade(company);
            this.companies = this.companies.filter((item) => item.id !== company.id);
            this.managers = this.managers.filter((manager) => manager.companyId !== company.id);
            if (this.selectedCompanyId === company.id) this.backToCompanies();
            this.showMessage(this.$t("admin.managers.deletedCompany"), "success");
          } catch (error) {
            this.showMessage(getUserErrorMessage(error, "admin.managers.companyDeleteError", this.$i18n), "error");
          } finally {
            this.deletingCompanyId = "";
          }
        },
        this.$t("admin.managers.deleteCompanyWarning")
      );
    },

    async deleteCompanyCascade(company) {
      const refsToDelete = new Map();
      const userUpdates = new Map();
      const companyId = company.id;
      const companyRef = doc(db, "companies", companyId);

      await this.addCompanyBoardsToDelete(refsToDelete, companyId);
      await this.addCollectionDocsToDelete(refsToDelete, collection(db, "companies", companyId, "workCalendars"));
      await this.addCollectionDocsToDelete(refsToDelete, collection(db, "companies", companyId, "workers"));
      await this.addCollectionDocsToDelete(
        refsToDelete,
        query(collection(db, "companyJoinRequests"), where("companyId", "==", companyId))
      );

      const companyUsersSnap = await getDocs(query(collection(db, "users"), where("companyId", "==", companyId)));
      companyUsersSnap.docs.forEach((userDoc) => {
        const data = userDoc.data();
        if (data.role === "admin") {
          refsToDelete.set(userDoc.ref.path, userDoc.ref);
        } else {
          userUpdates.set(userDoc.ref.path, {
            ref: userDoc.ref,
            data: {
              companyId: null,
              companyName: null,
              companyStatus: null,
              companyRequest: null,
            },
          });
        }
      });

      const companyRequestUsersSnap = await getDocs(
        query(collection(db, "users"), where("companyRequest.companyId", "==", companyId))
      );
      companyRequestUsersSnap.docs.forEach((userDoc) => {
        if (!refsToDelete.has(userDoc.ref.path)) {
          userUpdates.set(userDoc.ref.path, {
            ref: userDoc.ref,
            data: {
              companyId: null,
              companyName: null,
              companyStatus: null,
              companyRequest: null,
            },
          });
        }
      });

      const invitesSnap = await getDocs(query(collection(db, "adminInvites"), where("companyId", "==", companyId)));
      invitesSnap.docs.forEach((inviteDoc) => refsToDelete.set(inviteDoc.ref.path, inviteDoc.ref));
      refsToDelete.set(companyRef.path, companyRef);

      await this.commitDeleteAndUpdateBatches(
        Array.from(refsToDelete.values()),
        Array.from(userUpdates.values())
      );
    },

    async addCompanyBoardsToDelete(refsToDelete, companyId) {
      const boardsSnap = await getDocs(collection(db, "companies", companyId, "boards"));

      for (const boardDoc of boardsSnap.docs) {
        const noticesSnap = await getDocs(collection(boardDoc.ref, "notices"));
        noticesSnap.docs.forEach((noticeDoc) => refsToDelete.set(noticeDoc.ref.path, noticeDoc.ref));
        refsToDelete.set(boardDoc.ref.path, boardDoc.ref);
      }
    },

    async addCollectionDocsToDelete(refsToDelete, collectionRefOrQuery) {
      const snap = await getDocs(collectionRefOrQuery);
      snap.docs.forEach((documentSnapshot) => {
        refsToDelete.set(documentSnapshot.ref.path, documentSnapshot.ref);
      });
    },

    async commitDeleteAndUpdateBatches(deleteRefs, updates) {
      const operations = [
        ...deleteRefs.map((ref) => ({ type: "delete", ref })),
        ...updates.map((update) => ({ type: "update", ...update })),
      ];

      for (let index = 0; index < operations.length; index += batchLimit) {
        const batch = writeBatch(db);
        operations.slice(index, index + batchLimit).forEach((operation) => {
          if (operation.type === "delete") {
            batch.delete(operation.ref);
          } else {
            batch.update(operation.ref, operation.data);
          }
        });
        await batch.commit();
      }
    },

    deleteManager(manager) {
      const isLastManager = this.companyManagers.length <= 1;
      const warning = isLastManager
        ? this.$t("admin.managers.deleteLastManagerWarning")
        : "";

      this.openConfirm(
        this.$t("admin.managers.deleteConfirm", { name: manager.fullName || manager.email }),
        async () => {
          this.deletingManagerId = manager.id;

          try {
            const collectionName = manager.source === "invite" ? "adminInvites" : "users";
            await deleteDoc(doc(db, collectionName, manager.id));
            this.managers = this.managers.filter((item) => item.id !== manager.id);
            this.showMessage(this.$t("admin.managers.deletedManager"), "success");
          } catch (error) {
            this.showMessage(getUserErrorMessage(error, "admin.managers.managerDeleteError", this.$i18n), "error");
          } finally {
            this.deletingManagerId = "";
          }
        },
        warning
      );
    },

    openConfirm(message, action, warning = "") {
      this.confirmMessage = message;
      this.confirmWarning = warning;
      this.confirmAction = action;
      this.dialogConfirm = true;
    },

    async runConfirmAction() {
      if (!this.confirmAction) return;
      const action = this.confirmAction;
      this.dialogConfirm = false;
      await action();
      this.confirmAction = null;
      this.confirmMessage = "";
      this.confirmWarning = "";
    },

    formatDate(value) {
      if (!value) return "";
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString(this.$i18n.locale);
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    },
  },
};
</script>

<style scoped>
.v-card {
  background-color: #f8f7fd;
}

::v-deep .v-data-table tbody tr {
  cursor: pointer;
}

::v-deep .v-breadcrumbs__item {
  cursor: pointer;
}

::v-deep .v-breadcrumbs__item--disabled {
  cursor: default;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

@media (max-width: 600px) {
  .page-header,
  .company-header {
    align-items: stretch !important;
    flex-direction: column;
    gap: 12px;
  }

  .page-header .v-card__title,
  .company-header h2 {
    font-size: 1.2rem !important;
    line-height: 1.3;
  }

  .v-card {
    padding: 14px !important;
  }

  .v-btn-toggle {
    display: flex;
    width: 100%;
  }

  .v-btn-toggle .v-btn {
    flex: 1 1 0;
    min-width: 0 !important;
    padding: 0 8px !important;
  }

  ::v-deep .v-breadcrumbs {
    overflow-x: auto;
    white-space: nowrap;
  }
}
</style>
